"use client"

import styled from "styled-components"
import { Area, AreaChart, ResponsiveContainer, YAxis } from "recharts"
import {
  Badge,
  Card,
  CardHeader,
  MonoValue,
  StatGrid,
  StatLabel,
  StatValue,
  Subtitle,
  Title,
  TitleGroup,
} from "./primitives"

const pairData = [
  { t: 0, v: 1.082 },
  { t: 1, v: 1.079 },
  { t: 2, v: 1.084 },
  { t: 3, v: 1.088 },
  { t: 4, v: 1.086 },
  { t: 5, v: 1.091 },
  { t: 6, v: 1.094 },
]

const ChartWrap = styled.div`
  margin-top: 1.25rem;
  height: 7rem;
  width: 100%;
`

const strengths = [
  { ccy: "USD", pct: 78, tone: "var(--color-optimal)" },
  { ccy: "EUR", pct: 62, tone: "var(--color-optimal)" },
  { ccy: "GBP", pct: 54, tone: "var(--color-caution)" },
  { ccy: "JPY", pct: 33, tone: "var(--color-alert)" },
  { ccy: "AUD", pct: 41, tone: "var(--color-caution)" },
]

const MeterList = styled.div`
  margin-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const MeterRow = styled.div`
  display: grid;
  grid-template-columns: 3rem 1fr 2.5rem;
  align-items: center;
  gap: 0.75rem;
`

const MeterCcy = styled.span`
  font-family: var(--font-mono);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-card-foreground);
`

const MeterTrack = styled.div`
  height: 0.5rem;
  border-radius: 9999px;
  background: var(--color-muted);
  overflow: hidden;
`

const MeterFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  border-radius: 9999px;
  width: ${(p) => p.$pct}%;
  background: ${(p) => p.$color};
`

const MeterPct = styled.span`
  font-family: var(--font-mono);
  font-size: 0.8125rem;
  color: var(--color-muted-foreground);
  text-align: right;
`

export function ForexPairCard() {
  return (
    <Card>
      <CardHeader>
        <TitleGroup>
          <Title>EUR / USD</Title>
          <Subtitle>Euro · US Dollar</Subtitle>
        </TitleGroup>
        <Badge tone="optimal">+1.1% today</Badge>
      </CardHeader>

      <ChartWrap>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={pairData} margin={{ top: 8, right: 4, bottom: 0, left: 4 }}>
            <defs>
              <linearGradient id="fxFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-optimal)" stopOpacity={0.22} />
                <stop offset="100%" stopColor="var(--color-optimal)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <YAxis domain={["dataMin - 0.004", "dataMax + 0.004"]} hide />
            <Area
              type="monotone"
              dataKey="v"
              stroke="var(--color-optimal)"
              strokeWidth={2.5}
              fill="url(#fxFill)"
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartWrap>

      <StatGrid>
        <div>
          <StatLabel>Rate</StatLabel>
          <StatValue>1.0942</StatValue>
        </div>
        <div>
          <StatLabel>Day range</StatLabel>
          <StatValue>1.079–1.094</StatValue>
        </div>
        <div>
          <StatLabel>Session</StatLabel>
          <StatValue $tone="optimal">London</StatValue>
        </div>
      </StatGrid>
    </Card>
  )
}

export function CurrencyStrengthCard() {
  return (
    <Card>
      <CardHeader>
        <TitleGroup>
          <Title>Currency strength</Title>
          <Subtitle>Relative 24h momentum</Subtitle>
        </TitleGroup>
        <MonoValue>G5</MonoValue>
      </CardHeader>

      <MeterList>
        {strengths.map((s) => (
          <MeterRow key={s.ccy}>
            <MeterCcy>{s.ccy}</MeterCcy>
            <MeterTrack>
              <MeterFill $pct={s.pct} $color={s.tone} />
            </MeterTrack>
            <MeterPct>{s.pct}</MeterPct>
          </MeterRow>
        ))}
      </MeterList>
    </Card>
  )
}

export default function ForexCards() {
  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <ForexPairCard />
    </div>
  )
}
